import { useState } from "react";

import EditIcon from "./common/EditIcon";
import TrashIcon from "./common/TrashIcon";

const emptyForm = {
  id: "",
  name: "",
  vorname: "",
  username: "",
  password: "",
  isAdmin: false,
  bioApproval: false,
  tracesApproval: false,
  blocked: false,
  createdAt: "",
};

export default function Benutzerverwaltung({ users, onSaveUser, onDeleteUser }) {
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);

  const handleSave = async () => {
    if (!form.username.trim() || (!editId && !form.password.trim())) {
      window.alert("Bitte Benutzername und Passwort ausfüllen.");
      return;
    }

    if (form.password && form.password.length < 14) {
      window.alert("Das Passwort muss mindestens 14 Zeichen lang sein.");
      return;
    }

    const usernameAlreadyExists = users.some(
      (user) =>
        user.username.toLowerCase() === form.username.trim().toLowerCase() &&
        user.id !== editId
    );

    if (usernameAlreadyExists) {
      window.alert("Dieser Benutzername ist bereits vergeben.");
      return;
    }

    try {
      await onSaveUser({
        ...form,
        id: editId || "",
        username: form.username.trim(),
        name: form.name.trim(),
        vorname: form.vorname.trim(),
        createdAt: form.createdAt || new Date().toLocaleDateString("de-DE"),
      });

      setForm(emptyForm);
      setEditId(null);
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleEdit = (user) => {
    setForm({
      id: user?.id || "",
      name: user?.name || "",
      vorname: user?.vorname || "",
      username: user?.username || "",
      password: "",
      isAdmin: Boolean(user?.isAdmin),
      bioApproval: Boolean(user?.bioApproval),
      tracesApproval: Boolean(user?.tracesApproval),
      blocked: Boolean(user?.blocked),
      createdAt: user?.createdAt || "",
    });
    setEditId(user.id);
  };

  const handleDelete = async (userId) => {
    const userToDelete = users.find((user) => user.id === userId);

    if (userToDelete?.username === "admin") {
      window.alert("Der Standard-Admin kann nicht gelöscht werden.");
      return;
    }

    const confirmed = window.confirm(
      `Möchtest du den Benutzer "${userToDelete?.username || "unbekannt"}" wirklich löschen?`
    );

    if (!confirmed) {
      return;
    }

    try {
      await onDeleteUser(userId);
    } catch (error) {
      window.alert(error.message);
    }
  };

  return (
    <div className="page-stack">
      <div>
        <p className="eyebrow">Admin</p>
        <h1 className="page-title">Benutzerverwaltung</h1>
        <p className="section-text">
          Hier verwaltest du Benutzer, Passwörter und Berechtigungen.
        </p>
      </div>

      <section className="card">
        <h2>{editId ? "Benutzer bearbeiten" : "Neuen Benutzer anlegen"}</h2>

        <div className="form-grid columns-2">
          <label className="field">
            <span>Nachname</span>
            <input
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </label>

          <label className="field">
            <span>Vorname</span>
            <input
              value={form.vorname}
              onChange={(event) =>
                setForm({ ...form, vorname: event.target.value })
              }
            />
          </label>

          <label className="field">
            <span>Benutzername</span>
            <input
              value={form.username}
              onChange={(event) =>
                setForm({ ...form, username: event.target.value })
              }
            />
          </label>

          <label className="field">
            <span>Passwort</span>
            <input
              type="password"
              value={form.password}
              onChange={(event) =>
                setForm({ ...form, password: event.target.value })
              }
              minLength={14}
              autoComplete="new-password"
              placeholder={
                editId
                  ? "Leer lassen, um es nicht zu ändern (mind. 14 Zeichen)"
                  : "Mindestens 14 Zeichen"
              }
            />
          </label>
        </div>

        <div className="checkbox-row">
          <label className="checkbox-card">
            <input
              type="checkbox"
              checked={form.isAdmin}
              onChange={(event) =>
                setForm({
                  ...form,
                  isAdmin: event.target.checked,
                  bioApproval: event.target.checked
                    ? true
                    : form.bioApproval,
                  tracesApproval: event.target.checked
                    ? true
                    : form.tracesApproval,
                })
              }
            />
            Adminrechte vergeben
          </label>

          <label className="checkbox-card">
            <input
              type="checkbox"
              checked={form.isAdmin || form.bioApproval}
              disabled={form.isAdmin}
              onChange={(event) =>
                setForm({ ...form, bioApproval: event.target.checked })
              }
            />
            Bio Freigabe
          </label>

          <label className="checkbox-card">
            <input
              type="checkbox"
              checked={form.isAdmin || form.tracesApproval}
              disabled={form.isAdmin}
              onChange={(event) =>
                setForm({ ...form, tracesApproval: event.target.checked })
              }
            />
            Traces Freigabe
          </label>

          <label className="checkbox-card">
            <input
              type="checkbox"
              checked={form.blocked}
              onChange={(event) =>
                setForm({ ...form, blocked: event.target.checked })
              }
            />
            Benutzer sperren
          </label>
        </div>

        <div className="button-row">
          <button onClick={handleSave} className="button button-dark">
            {editId ? "Änderungen speichern" : "Benutzer speichern"}
          </button>

          <button
            onClick={() => {
              setForm(emptyForm);
              setEditId(null);
            }}
            className="button button-secondary"
          >
            Formular leeren
          </button>
        </div>
      </section>

      <section className="card">
        <h2>Benutzerliste</h2>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Benutzerkuerzel</th>
                <th>Adminrechte</th>
                <th>Bio Freigabe</th>
                <th>Traces Freigabe</th>
                <th>Status</th>
                <th>Angelegt am</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.isAdmin ? "Ja" : "Nein"}</td>
                  <td>{user.isAdmin || user.bioApproval ? "Ja" : "Nein"}</td>
                  <td>{user.isAdmin || user.tracesApproval ? "Ja" : "Nein"}</td>
                  <td>{user.blocked ? "Gesperrt" : "Aktiv"}</td>
                  <td>{user.createdAt || "-"}</td>
                  <td>
                    <div className="inline-actions">
                      <button
                        onClick={() => handleEdit(user)}
                        className="button button-inline-edit button-icon-edit"
                        aria-label="Bearbeiten"
                        title="Bearbeiten"
                      >
                        <EditIcon />
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="button button-inline-danger button-icon-danger"
                        aria-label="Loeschen"
                        title="Loeschen"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
